import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../Header';
import { appointmentMoveSteps } from '../bookingSteps';
import {
  formatAppointmentResponseBody,
  isSuccessfulAppointmentResponse,
  readAppointmentResponseBody,
} from '../appointmentResponse';
import { AppointmentResponseErrorDialog } from '../components/AppointmentResponseErrorDialog';
import { API_BASE_URL } from '../constants';
import { getBookingProcedureName } from '../bookingProcedureNames';
import { translations } from '../i18n/translations';
import { type CalendarAppointment } from '../appointmentCalendar';

interface PendingAppointmentFull {
  when: string;
  practitionerIds?: string[];
  procedureIds?: string[];
}

// slightly different, no arrays:
interface NewAppointmentFull {
  when: string;
  duration: number;
  practitionerId: string;
  patientId: string;
  procedureId: string;
  status: string;
  mode: string;
  description: string;
  // not used:
  //type: string;
  //appointmentInfoMap
  //meetingInfoMap
  //userIds // if userIds is given, practitionerId and patientId are ignored.
}

interface Procedure {
  id: string;
  name: string;
  duration: number;
  [ruleMap: string]: any;
}

interface AppointmentBookedSummary extends CalendarAppointment {
  when: string;
  procedureId?: string;
  procedureType?: string;
  procedureName: string;
  practitionerName: string;
}

const configureAppointmentSummaryHeader = () => {
  try {
    const headerDefinition = JSON.parse(localStorage.getItem('headerDefinition') ?? 'null');
    const headerPhase = localStorage.getItem('selectedAppointmentId')?.trim() ? '2' : Array.isArray(headerDefinition)
      ? headerDefinition.length === 3
        ? '2'
        : headerDefinition.length === 5
          ? '4'
          : null
      : null;

    if (headerPhase && localStorage.getItem('headerPhase') !== headerPhase) {
      localStorage.setItem('headerPhase', headerPhase);
    }
  } catch {
    // Keep the existing phase when the stored header definition is invalid.
  }
};

const readPendingAppointmentFull = (): PendingAppointmentFull | null => {
  try {
    const raw = localStorage.getItem('pendingAppointmentFull');
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as PendingAppointmentFull | null;
    if (!parsed?.when || typeof parsed.when !== 'string') {
      return null;
    }

    console.log("pendingAppointmentFull=" + JSON.stringify(parsed));
    //console.log("pendingAppointmentFull(parsed)=" + parsed)

    return parsed;
  } catch {
    return null;
  }
};

const readProcedureCache = () => {
  try {
    const raw = localStorage.getItem('procedureCache');
    if (!raw) {
      return {} as Record<string, Procedure>;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, Procedure>) : ({} as Record<string, Procedure>);
  } catch {
    return {} as Record<string, Procedure>;
  }
};

const writeProcedureCache = (cache: Record<string, Procedure>) => {
  try {
    localStorage.setItem('procedureCache', JSON.stringify(cache));
  } catch (error) {
    console.error('Failed to write procedure cache', error);
  }
};

const formatLongDate = (when: string) =>
  new Date(when).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const formatTime = (when: string) =>
  new Date(when).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDuration = (duration?: number) => {
  if (!duration || Number.isNaN(duration)) {
    return '';
  }

  return `${duration} Minuten`;
};

const readStoredStringArray = (key: string) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const getAppointmentDescription = (procedure?: Procedure | null) => {
  if (procedure?.id !== 'proc911' && procedure?.name.trim().toLowerCase() !== 'emergency') {
    return '';
  }

  const regionShortcuts: Record<string, string> = {
    upper: 'OK',
    lower: 'UK',
    left: 'li',
    right: 're',
  };
  const region = readStoredStringArray('selectedPainRegion')
    .map((part) => regionShortcuts[part])
    .filter(Boolean)
    .join(' ');
  const problems = readStoredStringArray('selectedPainProblems')
    .map((problem) => translations.de[problem] ?? problem)
    .join(',');

  return `${region}: ${problems}`;
};

export function AppointmentSummaryPage() {
  configureAppointmentSummaryHeader();

  const navigate = useNavigate();
  const [pendingAppointmentFull] = useState<PendingAppointmentFull | null>(() => readPendingAppointmentFull());
  const selectedAppointmentId = localStorage.getItem('selectedAppointmentId')?.trim() || '';
  const [practitionerName, setPractitionerName] = useState('');
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [moveErrorMessage, setMoveErrorMessage] = useState('');

  useEffect(() => {
    let ignore = false;

    const loadSummaryDetails = async () => {
      if (!pendingAppointmentFull) {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const practitionerId = pendingAppointmentFull.practitionerIds?.[0];
      const procedureId = pendingAppointmentFull.procedureIds?.[0];

      try {
        if (practitionerId && token) {
          const response = await fetch(`${API_BASE_URL}/practitioners/titled-name/${practitionerId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const contentType = response.headers.get('content-type') || '';
            const payload = contentType.includes('application/json')
              ? await response.json()
              : await response.text();

            const resolvedName =
              typeof payload === 'string'
                ? payload.trim()
                : typeof payload?.titledName === 'string'
                  ? payload.titledName.trim()
                  : typeof payload?.name === 'string'
                    ? payload.name.trim()
                    : typeof payload?.fullName === 'string'
                      ? payload.fullName.trim()
                      : '';

            if (!ignore) {
              setPractitionerName(resolvedName);
            }
          }
        }

        if (procedureId) {
          const procedureCache = readProcedureCache();
          const cachedProcedure = procedureCache[procedureId];

          if (cachedProcedure?.name) {
            if (!ignore) {
              setProcedure(cachedProcedure);
            }
            return;
          }

          if (token) {
            const response = await fetch(`${API_BASE_URL}/procedures/${procedureId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const loadedProcedure = (await response.json()) as Procedure;
              if (loadedProcedure?.id && loadedProcedure?.name) {
                procedureCache[procedureId] = loadedProcedure;
                writeProcedureCache(procedureCache);

                if (!ignore) {
                  setProcedure(loadedProcedure);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load appointment summary details', error);
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadSummaryDetails();

    return () => {
      ignore = true;
    };
  }, [pendingAppointmentFull]);

  const handleBack = () => {
    navigate('/date-time-entry');
  };

  const handleConfirm = async () => {

    setMoveErrorMessage('');

    console.log("handleConfirm: appointment=" + JSON.stringify(pendingAppointmentFull) + " submitting=" + submitting);

    if (!pendingAppointmentFull || submitting) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    setSubmitting(true);

    console.log("handleConfirm: " + selectedAppointmentId);

    try {
      // check if it is a move an existing Appointment or a new one:
      if (selectedAppointmentId) {
        // Move: TODO: always move Ergodent Appointment also:
        const moveResponse = await fetch(`${API_BASE_URL}/appointments/move`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            appointmentId: selectedAppointmentId,
            when: pendingAppointmentFull.when,
            mode: "ergodent:demo",
          }),
        });

        const moveResponseBody = await readAppointmentResponseBody(moveResponse);

        // handleConfirm: moveResponse={"ok":true,"status":200,"body":"ok"}
        // handleConfirm: moveResponse={"ok":true,"status":200,"body":"ERROR-ERGODENT:{\"title\":\"ERROR\",\"TYPE\":\"POST\",\"statusCode\":\"400 BAD_REQUEST\",\"BODY\":{\"Code\":7,\"Message\":\"Appointment starts too early\"}}"}

        console.log("handleConfirm: moveResponse=" + JSON.stringify({
          ok: moveResponse.ok,
          status: moveResponse.status,
          body: moveResponseBody,
        }));

        const moveSuccessful = isSuccessfulAppointmentResponse(moveResponse, moveResponseBody);
        if (!moveSuccessful) {
          const formattedMoveResponseBody = formatAppointmentResponseBody(moveResponseBody);

          console.log("handleConfirm: bad response=" + JSON.stringify({
            ok: moveResponse.ok,
            status: moveResponse.status,
            body: formattedMoveResponseBody,
          }));

          setMoveErrorMessage(`Error: ${formattedMoveResponseBody}`);
          return;
        }

        if (typeof moveResponseBody === 'string') {
          localStorage.setItem('lastMoveResponse', moveResponseBody);
        } else {
          localStorage.setItem('lastMoveResponse', JSON.stringify(moveResponseBody));
        }

        const appointmentResponse = await fetch(`${API_BASE_URL}/appointments/get-full-appointment/${selectedAppointmentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!appointmentResponse.ok) {
          return;
        }

        const movedAppointment = await appointmentResponse.json();
        localStorage.setItem('lastMovedAppointment', JSON.stringify(movedAppointment));
        localStorage.removeItem('pendingAppointmentFull');
        navigate('/move-conf', { state: { appointment: movedAppointment } });
        return;
      }

      // New Appointment, save it:
      const patientId = localStorage.getItem('loginPatientId')?.trim() || ''; // TODO: or selectedPatientId ??
      const practitionerId = pendingAppointmentFull.practitionerIds?.[0]?.trim() || '';
      const procedureId = procedure?.id?.trim() || pendingAppointmentFull.procedureIds?.[0]?.trim() || '';
      const duration = procedure?.duration;

      console.log("handleConfirm: " + patientId + " " + practitionerId + " " + procedureId + " " + duration);

      if (!patientId || !practitionerId || !procedureId || !duration) {
        console.log("handleConfirm: MISSING INFO: patientId=" + patientId + " practitionerId=" + practitionerId + " procedureId=" + procedureId + " duration=" + duration);
        return;
      }

      // TODO: always save to Ergodent also:
      const payload: NewAppointmentFull = {
        when: pendingAppointmentFull.when,
        duration,
        practitionerId,
        patientId,
        procedureId,
        status: 'confirmed',
        mode: "ergodent:demo",
        description: getAppointmentDescription(procedure),
      };

      const saveResponse = await fetch(`${API_BASE_URL}/appointments/save-full`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const saveResponseBody = await readAppointmentResponseBody(saveResponse);
      const saveSuccessful = isSuccessfulAppointmentResponse(saveResponse, saveResponseBody);

      if (!saveSuccessful) {
        const formattedSaveResponseBody = formatAppointmentResponseBody(saveResponseBody);

        console.log("New Appointment FAILED: " + JSON.stringify({
          payload,
          ok: saveResponse.ok,
          status: saveResponse.status,
          body: formattedSaveResponseBody,
        }));

        setMoveErrorMessage(`Error: ${formattedSaveResponseBody}`);
        return;
      }

      console.log("New Appointment " + JSON.stringify(payload));

      const bookedSummary: AppointmentBookedSummary = {
        // save-full returns "ok <appointment ID>" after the confirmed save.
        id: typeof saveResponseBody === 'string' ? /^ok\s+(\S+)\s*$/.exec(saveResponseBody)?.[1] : undefined,
        when: pendingAppointmentFull.when,
        duration: payload.duration,
        procedureId,
        procedureType: procedure?.name,
        procedureName: getBookingProcedureName(procedure?.name, procedure?.id),
        practitionerName: practitionerName || 'Unbekannt',
      };

      localStorage.setItem('lastBookedAppointmentSummary', JSON.stringify(bookedSummary));
      localStorage.removeItem('pendingAppointmentFull');
      navigate('/appointment-booked', { state: { appointment: bookedSummary } });
    } catch (error) {
      console.error('Failed to confirm appointment', error);
      if (selectedAppointmentId) {
        setMoveErrorMessage(`Error: ${error instanceof Error ? error.message : String(error)}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!pendingAppointmentFull) {
    return (
      <div className="booking-summary min-h-screen bg-[#fbf9f5] text-slate-900">
        <Header compact steps={selectedAppointmentId ? appointmentMoveSteps : undefined} phase={selectedAppointmentId ? 2 : undefined} />
        <div className="mx-auto flex min-h-[calc(100vh-12rem)] max-w-5xl items-center justify-center px-6 pb-12">
          <div className="w-full max-w-3xl rounded-[32px] bg-white px-8 py-12 text-center shadow-[0_25px_55px_rgba(19,19,19,0.08)]">
            <h1 className="text-3xl font-semibold text-slate-900">Keine Auswahl gefunden</h1>
            <p className="mx-auto mt-3 max-w-lg text-sm text-slate-500">
              Bitte wählen Sie zuerst ein Datum und eine Uhrzeit aus.
            </p>
            <button
              onClick={handleBack}
              className="mt-8 rounded-2xl border border-[#e4d7c3] bg-white px-6 py-4 text-sm font-semibold text-slate-600"
            >
              Zurück
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-summary min-h-screen bg-[#fbf9f5] text-slate-900">
      <Header compact steps={selectedAppointmentId ? appointmentMoveSteps : undefined} phase={selectedAppointmentId ? 2 : undefined} />
      <AppointmentResponseErrorDialog message={moveErrorMessage} onClose={() => setMoveErrorMessage('')} />
      <div className="booking-summary__content mx-auto max-w-[840px] px-6 pb-12 pt-10">
        <div className="booking-summary__card w-full rounded-[32px] bg-white p-6 shadow-[0_25px_55px_rgba(19,19,19,0.04)] sm:p-10">
          <h1 className="break-words text-2xl font-semibold leading-tight text-slate-950 sm:text-[30px]">Terminzusammenfassung</h1>
          <p className="mt-3 text-base leading-6 text-slate-400">Bitte überprüfen Sie Ihre Auswahl.</p>

          <div className="booking-summary__details mt-8 rounded-[28px] border border-[#efe7da] bg-[#fbfaf7] p-5 sm:p-7">
            <div className="border-b border-[#efe7da] pb-6">
              <p className="text-sm font-semibold tracking-wide text-slate-400">Behandlung</p>
              <p className="mt-3 text-xl font-semibold leading-7 text-slate-900">
                {loading ? '...' : getBookingProcedureName(procedure?.name, procedure?.id)}
              </p>
              <p className="mt-2 text-base leading-6 text-slate-400">{formatDuration(procedure?.duration)}</p>
            </div>

            <div className="border-b border-[#efe7da] py-6">
              <p className="text-sm font-semibold tracking-wide text-slate-400">Behandler</p>
              <p className="mt-3 text-xl font-semibold leading-7 text-slate-900">
                {loading ? '...' : practitionerName || 'Unbekannt'}
              </p>
            </div>

            <div className="flex flex-col items-start justify-between gap-4 pt-6 sm:flex-row sm:gap-8">
              <div>
                <p className="text-sm font-semibold tracking-wide text-slate-400">Datum & Uhrzeit</p>
                <p className="mt-3 text-lg font-semibold leading-7 text-slate-900">
                  {formatLongDate(pendingAppointmentFull.when)}
                </p>
                <p className="mt-2 text-lg font-semibold leading-7 text-slate-900">
                  {formatTime(pendingAppointmentFull.when)} Uhr
                </p>
              </div>
              <button
                onClick={handleBack}
                className="text-base font-semibold text-[#cbb58a] transition hover:text-[#bca477] sm:pt-8"
              >
                Ändern
              </button>
            </div>
          </div>
        </div>

        <div className="booking-summary__actions mt-10 flex flex-col-reverse gap-6 sm:flex-row sm:justify-end">
          <button
            onClick={handleBack}
            className="inline-flex h-[52px] min-w-[205px] items-center justify-center gap-3 rounded-xl border border-[#d8d8d8] bg-white px-[26px] text-[17px] font-semibold text-slate-500 shadow-[0_12px_20px_rgba(19,19,19,0.04)] transition hover:border-[#cbb58a]"
          >
            <span aria-hidden="true">&larr;</span>
            Zurück
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="h-[52px] min-w-[205px] rounded-xl bg-[#cbb58a] px-[26px] text-[17px] font-semibold text-white shadow-[0_10px_20px_rgba(203,181,138,0.24)] transition hover:bg-[#bca477] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Termin bestätigen
          </button>
        </div>
      </div>
    </div>
  );
}
